import * as React from 'react';
import { ApolloClient, InMemoryCache, ApolloLink, HttpLink, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createClient } from 'graphql-ws';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { Kind, OperationTypeNode } from 'graphql';
import { RetryLink } from '@apollo/client/link/retry';
import { useAppSettings } from './AppSettings';
import { useInstrumentation } from './Instrumentation';
import { useMidwayToken } from './Midway';
import { useAddNotification } from './Notifications';
import { TimeoutLink } from '../utils/TimeoutLink';
import { AppSyncSubscriptionLink } from '../utils/AppSyncSubscriptionLink';
import { isAntennaNotFoundMessage } from '../utils/antennaDataProductErrors';
import { DataProductApi } from '../constants/api';
import { useDataProductEndpoints } from '../hooks/useDataProductEndpoints';
import type { GsocApolloClients } from './GsocApolloHooks';
import { GsocApolloContext } from './GsocApolloHooks';
import { scalarsLinkForApi } from '../graphql/dataProductSchemas';

// Operations whose fields may not exist in every stage's deployed schema yet. Their
// FieldUndefined errors are expected, so we skip the console log and notification;
// the query hooks fall back via errorPolicy 'all'.
const BEST_EFFORT_OPERATIONS = new Set<string>(['contactLaminarFlowReport']);

export function GsocApolloProvider(props: { readonly children: React.ReactNode }) {
  const { graphqlApiUrl } = useAppSettings();
  const apiEndpointConfig = useDataProductEndpoints();
  const midwayToken = useMidwayToken();
  const addNotification = useAddNotification();
  const { rumClient } = useInstrumentation();

  const errorLink = React.useMemo(
    () =>
      onError(({ graphQLErrors, networkError, operation }) => {
        if (networkError) {
          if ('statusCode' in networkError && networkError.statusCode === 401) {
            return;
          }
          rumClient?.recordError(networkError);
          console.error(`[Network error]: ${networkError.message}`, networkError);
          addNotification({
            header: 'Network error',
            message: networkError.message,
            type: 'error',
          });
        } else if (!BEST_EFFORT_OPERATIONS.has(operation.operationName)) {
          // Operations can mark not-found as expected so it does not toast,
          // the caller still receives the error for its own empty state.
          const suppressNotFound = operation.getContext()['suppressNotFoundNotification'] === true;
          for (const graphqlError of graphQLErrors ?? []) {
            if (suppressNotFound && isAntennaNotFoundMessage(graphqlError.message)) {
              continue;
            }
            rumClient?.recordError(graphqlError);
            console.error(
              `[GraphQL error]: Operation: ${operation.operationName}, Message: ${graphqlError.message}, Location: ${String(graphqlError.locations)}, Path: ${String(graphqlError.path)}`,
            );
            addNotification({
              header: 'GraphQL error',
              message: graphqlError.message,
              type: 'error',
            });
          }
        }
      }),
    [rumClient, addNotification],
  );

  const unauthorizedHandlerLink = React.useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        const result = forward(operation);
        return result.map(({ data, errors }) => {
          const filteredErrors =
            errors?.filter(error => (error as unknown as { errorType?: string }).errorType !== 'Unauthorized') ?? [];
          return {
            data,
            ...(filteredErrors.length > 0 ? { errors: filteredErrors } : {}),
          };
        });
      }),
    [],
  );

  const retryLink = React.useMemo(
    () =>
      new RetryLink({
        delay: { initial: 300, max: Infinity, jitter: true },
        attempts: {
          max: 5,
          retryIf: (networkError: object, _operation) => {
            if (isNetworkErrorWithStatusCode(networkError)) {
              return [408, 435, 429, 502, 503, 504].includes(networkError.statusCode);
            }
            return true;
          },
        },
      }),
    [],
  );

  const timeoutLink = React.useMemo(() => new TimeoutLink({ timeoutMs: 8000 }), []);

  const sharedLinks = React.useMemo(
    () => ApolloLink.from([errorLink, unauthorizedHandlerLink, retryLink, timeoutLink]),
    [errorLink, unauthorizedHandlerLink, retryLink, timeoutLink],
  );

  const createHttpLinkForApi = React.useCallback(
    (api: DataProductApi) =>
      new HttpLink({
        uri: apiEndpointConfig[api],
        headers: { Authorization: `Bearer ${midwayToken}` },
      }),
    [midwayToken, apiEndpointConfig],
  );

  const wsLink = React.useMemo(
    () =>
      new GraphQLWsLink(
        createClient({
          url: graphqlApiUrl,
          connectionParams: { headers: { Authorization: `Bearer ${midwayToken}` } },
        }),
      ),
    [midwayToken, graphqlApiUrl],
  );

  // AppSync realtime subscriptions require a custom auth protocol:
  // wss://{host}/graphql/realtime?header={base64({"host","Authorization"})}&payload={base64({})}
  // with sub-protocol "graphql-ws", followed by connection_init/ack/start/data messages.
  // The standard graphql-ws createClient does not implement this — we use AppSyncSubscriptionLink.
  // midwayToken is captured by reference via a ref so the link instance is stable across
  // token refreshes (the token is re-read from the ref on each new subscription).
  const midwayTokenRef = React.useRef(midwayToken);
  React.useEffect(() => {
    midwayTokenRef.current = midwayToken;
  }, [midwayToken]);

  const antennaDpEndpointRef = React.useRef(apiEndpointConfig[DataProductApi.ANTENNA_DATA_PRODUCT_API]);
  React.useEffect(() => {
    antennaDpEndpointRef.current = apiEndpointConfig[DataProductApi.ANTENNA_DATA_PRODUCT_API];
  }, [apiEndpointConfig]);

  // Stable link instance — url and getAuthHeader read from refs so it never needs to be recreated.
  const antennaDpSubscriptionLink = React.useMemo(
    () =>
      new AppSyncSubscriptionLink({
        get url() {
          return antennaDpEndpointRef.current;
        },
        getAuthHeader: () => `Bearer ${midwayTokenRef.current}`,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const createLinkForDataProductApi = React.useCallback(
    (api: DataProductApi) => {
      const httpLink = createHttpLinkForApi(api);
      if (api === DataProductApi.CURATED_API || api === DataProductApi.ANTENNA_DATA_PRODUCT_API) {
        const subscriptionLink = api === DataProductApi.CURATED_API ? wsLink : antennaDpSubscriptionLink;
        const splitLink = split(
          ({ query }) => {
            const definition = getMainDefinition(query);
            return (
              definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION
            );
          },
          subscriptionLink,
          httpLink,
        );
        return ApolloLink.from([scalarsLinkForApi[api], sharedLinks, splitLink]);
      }
      return ApolloLink.from([scalarsLinkForApi[api], sharedLinks, httpLink]);
    },
    [sharedLinks, createHttpLinkForApi, wsLink, antennaDpSubscriptionLink],
  );

  const clients = React.useMemo<GsocApolloClients>(
    () => ({
      [DataProductApi.CURATED_API]: new ApolloClient({
        cache: new InMemoryCache({
          typePolicies: {
            GsocContactV2: { keyFields: ['contactId'] },
            GsocDataflowV2: {
              keyFields: ['dataflowId', 'destination', ['config', ['id']], 'source', ['config', ['id']]],
            },
            GsocCapabilityV2: { keyFields: ['id'] },
          },
        }),
        link: createLinkForDataProductApi(DataProductApi.CURATED_API),
        devtools: { enabled: true, name: DataProductApi.CURATED_API },
        defaultOptions: { query: { errorPolicy: 'all', returnPartialData: true } },
      }),
      [DataProductApi.DATA_PLANE_API]: new ApolloClient({
        cache: new InMemoryCache(),
        link: createLinkForDataProductApi(DataProductApi.DATA_PLANE_API),
        devtools: { enabled: true, name: DataProductApi.DATA_PLANE_API },
        defaultOptions: { query: { errorPolicy: 'all', returnPartialData: true } },
      }),
      [DataProductApi.CONTROL_PLANE_DATA_PRODUCT_API]: new ApolloClient({
        cache: new InMemoryCache({
          typePolicies: {
            Contact: { keyFields: contact => `${contact['contactId'] as string}:${contact['version'] as number}` },
          },
        }),
        link: createLinkForDataProductApi(DataProductApi.CONTROL_PLANE_DATA_PRODUCT_API),
        devtools: { enabled: true, name: DataProductApi.CONTROL_PLANE_DATA_PRODUCT_API },
        defaultOptions: { query: { errorPolicy: 'all', returnPartialData: true } },
      }),
      [DataProductApi.ANTENNA_DATA_PRODUCT_API]: new ApolloClient({
        cache: new InMemoryCache(),
        link: createLinkForDataProductApi(DataProductApi.ANTENNA_DATA_PRODUCT_API),
        defaultOptions: { query: { errorPolicy: 'all', returnPartialData: true } },
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Update data product links when midway token or endpoints change
  React.useEffect(() => {
    for (const api of Object.values(DataProductApi)) {
      clients[api].setLink(createLinkForDataProductApi(api));
    }
  }, [clients, createLinkForDataProductApi]);

  // GC all caches periodically
  React.useEffect(() => {
    const intervalHandle = setInterval(
      () => {
        for (const api of Object.values(DataProductApi)) {
          clients[api].cache.gc();
        }
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(intervalHandle);
  }, [clients]);

  return <GsocApolloContext.Provider value={clients}>{props.children}</GsocApolloContext.Provider>;
}

function isNetworkErrorWithStatusCode(err: object): err is { statusCode: number } {
  return 'statusCode' in err;
}
